/**
 * Sized cover rungs (NFR-FE-001.4).
 *
 * The backend serves a downscaled WebP rendition from `GET /api/books/{id}/cover.png?w=<n>`. The
 * *server* picks the format, so this is a plain `srcSet` on the same `.png` URL — no `<picture>`,
 * no `type="image/webp"` source, no fallback needed. A width with no variant built (a cover
 * predating the feature, a source too small for that rung) transparently serves the original, so
 * every URL here is always valid: a miss costs bytes, never a broken image.
 *
 * Resize is the bigger half of the win, not the codec — full-size PNG->WebP is ~12x, but
 * resize+WebP is ~76x because they multiply. Shipping a 1200px cover into a 165px card was the
 * actual bug.
 */

/** Must stay in step with `LADDER` in `backend/app/services/cover_variants.py`. */
export const COVER_RUNGS = [128, 400, 560, 800] as const;

/** Cover URLs we control. An external pick (`http(s)://…`, passed through verbatim) has no `?w=`. */
function isAppCover(url: string): boolean {
  return url.startsWith("/api/books/");
}

/**
 * A `srcSet` for a cover URL, or `undefined` when the URL isn't ours to resize — returning
 * `undefined` leaves the `<img>` with just its `src`, which is the correct degradation.
 *
 * Tolerates a URL that already carries a query (the editor appends `?v=<bust>` to force a
 * refetch after a regenerate) by joining with `&`.
 */
export function coverSrcSet(url: string | null | undefined): string | undefined {
  if (!url || !isAppCover(url)) return undefined;
  const sep = url.includes("?") ? "&" : "?";
  return COVER_RUNGS.map((w) => `${url}${sep}w=${w} ${w}w`).join(", ");
}

/**
 * A single rung, for fixed-width sites that don't need a full `srcSet`.
 * Falls back to the bare URL when it isn't ours (external pick).
 */
export function coverAtWidth(url: string | null | undefined, width: number): string | undefined {
  if (!url) return undefined;
  if (!isAppCover(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}w=${width}`;
}
